import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { RFQ, Quotation } from '@/types/quotations';
import { formatCurrency, formatDate } from './format';

// Generate RFQ PDF
export const generateRFQPDF = (rfq: RFQ) => {
  const doc = new jsPDF();

  // Add header
  doc.setFontSize(20);
  doc.text('Request for Quotation', doc.internal.pageSize.width / 2, 20, { align: 'center' });

  // Add RFQ details
  doc.setFontSize(12);
  doc.text(`RFQ Number: ${rfq.rfq_number}`, 20, 40);
  doc.text(`Date: ${formatDate(rfq.date)}`, 20, 50);
  doc.text(`Required Date: ${formatDate(rfq.required_date)}`, 20, 60);

  if (rfq.pr) {
    doc.text('PR Reference:', 120, 40);
    doc.text(rfq.pr.pr_number, 120, 50);
  }

  // Add supplier section
  doc.setFontSize(14);
  doc.text('Selected Suppliers:', 20, 80);
  doc.setFontSize(12);
  let yPos = 90;
  rfq.suppliers?.forEach(s => {
    doc.text(`• ${s.supplier.name}`, 30, yPos);
    yPos += 10;
  });

  // Add items table
  autoTable(doc, {
    startY: yPos + 10,
    head: [['Item Code', 'Item Name', 'Description', 'Quantity', 'Unit', 'Specifications']],
    body: rfq.items?.map(item => [
      item.item_code,
      item.item_name,
      item.description || '',
      item.quantity.toString(),
      item.unit,
      item.specifications || ''
    ]) || [],
    headStyles: { fillColor: [51, 51, 51] }
  });

  // Add terms and conditions
  if (rfq.terms_conditions) {
    doc.addPage();
    doc.setFontSize(14);
    doc.text('Terms and Conditions', 20, 20);
    doc.setFontSize(12);
    doc.text(rfq.terms_conditions, 20, 30);
  }

  // Add notes
  if (rfq.notes) {
    const finalY = (doc as any).lastAutoTable.finalY || doc.internal.pageSize.height - 40;
    doc.setFontSize(14);
    doc.text('Notes', 20, finalY + 20);
    doc.setFontSize(12);
    doc.text(rfq.notes, 20, finalY + 30);
  }

  return doc;
};

// Generate Quotation PDF
export const generateQuotationPDF = (quotation: Quotation) => {
  const doc = new jsPDF();

  // Add header
  doc.setFontSize(20);
  doc.text('Supplier Quotation', doc.internal.pageSize.width / 2, 20, { align: 'center' });

  // Add quotation details
  doc.setFontSize(12);
  doc.text(`Quotation Number: ${quotation.quotation_number}`, 20, 40);
  doc.text(`Date: ${formatDate(quotation.date)}`, 20, 50);
  doc.text(`Valid Until: ${formatDate(quotation.valid_until)}`, 20, 60);

  if (quotation.supplier) {
    doc.text('Supplier Details:', 120, 40);
    doc.text(quotation.supplier.name, 120, 50);
  }

  // Add items table
  autoTable(doc, {
    startY: 80,
    head: [['Item Code', 'Description', 'Quantity', 'Unit', 'Unit Price', 'Tax', 'Total']],
    body: quotation.items?.map(item => [
      item.item_code,
      item.description || '',
      item.quantity.toString(),
      item.unit,
      formatCurrency(item.unit_price),
      item.tax_rate ? `${item.tax_rate}%` : '-',
      formatCurrency(item.total_amount)
    ]) || [],
    foot: [
      [{ content: 'Subtotal:', colSpan: 6, styles: { halign: 'right', fontStyle: 'bold' } }, formatCurrency(quotation.subtotal)],
      [{ content: 'Tax Amount:', colSpan: 6, styles: { halign: 'right', fontStyle: 'bold' } }, formatCurrency(quotation.tax_amount)],
      [{ content: 'Total Amount:', colSpan: 6, styles: { halign: 'right', fontStyle: 'bold' } }, `${formatCurrency(quotation.total_amount)} ${quotation.currency}`]
    ],
    headStyles: { fillColor: [51, 51, 51] }
  });

  // Add terms section
  let yPos = (doc as any).lastAutoTable.finalY + 20;

  if (quotation.payment_terms) {
    doc.setFontSize(14);
    doc.text('Payment Terms', 20, yPos);
    doc.setFontSize(12);
    doc.text(quotation.payment_terms, 20, yPos + 10);
    yPos += 30;
  }

  if (quotation.delivery_terms) {
    doc.setFontSize(14);
    doc.text('Delivery Terms', 20, yPos);
    doc.setFontSize(12);
    doc.text(quotation.delivery_terms, 20, yPos + 10);
    yPos += 30;
  }

  if (quotation.warranty_terms) {
    doc.setFontSize(14);
    doc.text('Warranty Terms', 20, yPos);
    doc.setFontSize(12);
    doc.text(quotation.warranty_terms, 20, yPos + 10);
    yPos += 30;
  }

  // Add notes
  if (quotation.notes) {
    doc.setFontSize(14);
    doc.text('Notes', 20, yPos);
    doc.setFontSize(12);
    doc.text(quotation.notes, 20, yPos + 10);
  }

  return doc;
};